import PropTypes from 'prop-types';

/**
 * The icon buttons at the end of a table row — edit, delete, view, whatever the
 * row offers.
 *
 * Written once because it was written thirty-two times. Every tab that lists
 * anything ends its rows with the same pair of buttons, each tab spelling out
 * its own Tailwind string, and they had drifted: different sizes, different
 * radii, some with a border and some without. A shop that moves from Products
 * to Suppliers should not have to re-learn what a button looks like.
 *
 * Copied from the Super Admin master tables — State Master, District Master,
 * Organizations — down to the pixel: 28px, small radius, no fill and no border
 * until the pointer arrives, and then a white tile with a light border and a
 * small shadow, as though the button lifted off the row. That console is where
 * this pattern already works over hundreds of rows, so it is the reference
 * rather than something re-decided here.
 *
 * The border is transparent rather than absent, which is the detail that makes
 * it work: the button occupies its full size from the start, so nothing shifts
 * or reflows when a row is hovered. A whole column of outlined boxes is a lot
 * of ink for controls only one row at a time can use.
 *
 * Nothing here is word-labelled. A column of repeated EDIT / DELETE reads as
 * noise once there are eight rows of it; the cards, which show one of each,
 * keep their words.
 */

// The shell every one of them shares: size, radius, the transparent border it
// holds space with, and the white tile it becomes on hover.
const SHELL =
  'w-7 h-7 shrink-0 inline-flex items-center justify-center rounded bg-transparent '
  + 'border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 '
  + 'hover:shadow-sm transition-all outline-none';

// Only the colour the icon turns differs, and it says what the button does —
// not how the row is doing. A list drawn with a red button and a green one in
// every row reads as a list of problems and approvals, when it is neither.
const TONE = {
  // The ordinary action — edit, view, open.
  default: 'hover:text-[var(--theme-primary)]',
  // Anything that destroys a row.
  danger: 'hover:text-rose-600',
  // Accepting something: committing pending stock, approving a request.
  success: 'hover:text-emerald-600',
  // Moving something somewhere it can be undone from — a transfer back to
  // pending, say. Not a deletion, but not nothing either.
  warn: 'hover:text-orange-500',
};

const OFF =
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent '
  + 'disabled:hover:text-slate-500 disabled:hover:border-transparent disabled:hover:shadow-none';

export const IconAction = ({ icon: Icon, label, onClick, tone = 'default', disabled = false, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={title || label}
    className={`${SHELL} ${TONE[tone] || TONE.default} ${OFF}`}
  >
    <Icon size={14} aria-hidden="true" />
  </button>
);

IconAction.propTypes = {
  icon: PropTypes.elementType.isRequired,
  /** Read aloud, and shown on hover unless `title` says otherwise. Name the
   *  row: "Delete Cashier", not "Delete" — a screen reader moving down the
   *  column otherwise hears the same word twenty times. */
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  tone: PropTypes.oneOf(['default', 'danger', 'success', 'warn']),
  disabled: PropTypes.bool,
  /** Use when a disabled button should say why it is disabled. */
  title: PropTypes.string,
};

/**
 * The row of them, spaced and pushed to the right edge of the cell.
 *
 * `onClick` is here for the lists whose whole row opens something. Pressing a
 * button inside such a row would otherwise open the row as well as doing what
 * the button says, so those tabs stop the event at this wrapper.
 */
export const RowActions = ({ children, onClick }) => (
  <div className="flex items-center justify-end gap-2" onClick={onClick}>{children}</div>
);

RowActions.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
};
